import React from 'react';
import { BadgeInfo, BookMarked, Loader2, ReceiptText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { formatAmountInput, parseAmountDigits } from '@/lib/formatMoney';
import { bsiInternetBankApi, type BsiIbAccountOption } from '@/lib/banks/bsi/api';
import { isSessionExpiredMessage, type BsiFlashMessage } from './helpers';

type Props = {
  sessionValid: boolean;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
};

type Mode = 'status' | 'chequebook' | 'amount';

const BsiChequeTab: React.FC<Props> = ({ sessionValid, onMessage, onSessionExpired }) => {
  const [mode, setMode] = React.useState<Mode>('status');
  const [loadingForm, setLoadingForm] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [accounts, setAccounts] = React.useState<BsiIbAccountOption[]>([]);
  const [currencies, setCurrencies] = React.useState<BsiIbAccountOption[]>([]);
  const [accountNo, setAccountNo] = React.useState('');
  const [currency, setCurrency] = React.useState('RIAL');
  const [chequeNo, setChequeNo] = React.useState('');
  const [indexChequeNo, setIndexChequeNo] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [result, setResult] = React.useState<{ status?: string | null; fields?: Record<string, string> | null; raw?: string | null } | null>(null);

  const loadForm = React.useCallback(
    async (nextMode?: Mode) => {
      const useMode = nextMode || mode;
      setLoadingForm(true);
      setResult(null);
      onMessage(null);
      try {
        const res =
          useMode === 'status'
            ? await bsiInternetBankApi.chequeStatusForm()
            : useMode === 'chequebook'
              ? await bsiInternetBankApi.chequebookStatusForm()
              : await bsiInternetBankApi.chequeAmountForm();
        setAccounts(res.data.accounts || []);
        setCurrencies(res.data.currencies || []);
        setAccountNo(res.data.accounts?.[0]?.value || '');
        setCurrency(res.data.currencies?.[0]?.value || 'RIAL');
        setChequeNo('');
        setIndexChequeNo('');
        setAmount('');
      } catch (e) {
        const text = e instanceof Error ? e.message : 'دریافت فرم چک ناموفق بود.';
        onMessage({ type: 'error', text });
        if (isSessionExpiredMessage(text)) onSessionExpired();
      } finally {
        setLoadingForm(false);
      }
    },
    [mode, onMessage, onSessionExpired],
  );

  React.useEffect(() => {
    if (sessionValid && accounts.length === 0 && !loadingForm) void loadForm('status');
  }, [sessionValid, accounts.length, loadingForm, loadForm]);

  const handleSubmit = async () => {
    if (!accountNo) {
      onMessage({ type: 'error', text: 'حساب را انتخاب کنید.' });
      return;
    }
    setSubmitting(true);
    setResult(null);
    onMessage(null);
    try {
      const res =
        mode === 'status'
          ? await bsiInternetBankApi.chequeStatus({
              account_no: accountNo,
              cheque_no: chequeNo.trim(),
              currency,
            })
          : mode === 'chequebook'
            ? await bsiInternetBankApi.chequebookStatus({
                account_no: accountNo,
                index_cheque_no: indexChequeNo.trim(),
                currency,
              })
            : await bsiInternetBankApi.chequeAmountPreview({
                account_no: accountNo,
                cheque_no: chequeNo.trim(),
                amount: parseAmountDigits(amount),
                currency,
              });
      setResult(res.data);
      onMessage({ type: 'success', text: res.message || 'عملیات چک انجام شد.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'عملیات چک ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setSubmitting(false);
    }
  };

  const fields = result?.fields ? Object.entries(result.fields) : [];

  return (
    <Card className="lg:col-span-3">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ReceiptText className="h-4 w-4" />
          خدمات چک
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!sessionValid ? (
          <p className="text-sm text-amber-700">برای خدمات چک ابتدا وارد اینترنت‌بانک شوید.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Button type="button" variant={mode === 'status' ? 'default' : 'outline'} onClick={() => { setMode('status'); void loadForm('status'); }}>
                <BadgeInfo className="ml-1 h-4 w-4" />
                وضعیت چک
              </Button>
              <Button type="button" variant={mode === 'chequebook' ? 'default' : 'outline'} onClick={() => { setMode('chequebook'); void loadForm('chequebook'); }}>
                <BookMarked className="ml-1 h-4 w-4" />
                وضعیت دسته‌چک
              </Button>
              <Button type="button" variant={mode === 'amount' ? 'default' : 'outline'} onClick={() => { setMode('amount'); void loadForm('amount'); }}>
                <ReceiptText className="ml-1 h-4 w-4" />
                مبلغ چک
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label>حساب</Label>
                <SearchableSelect
                  value={accountNo}
                  onChange={setAccountNo}
                  options={accounts.map((a) => ({ value: a.value, label: a.label || a.value }))}
                  placeholder="انتخاب حساب"
                  disabled={loadingForm}
                />
              </div>
              <div className="space-y-2">
                <Label>ارز</Label>
                <SearchableSelect
                  value={currency}
                  onChange={setCurrency}
                  options={currencies.map((c) => ({ value: c.value, label: c.label || c.value }))}
                  placeholder="انتخاب ارز"
                  disabled={loadingForm}
                />
              </div>

              {mode !== 'chequebook' && (
                <div className="space-y-2">
                  <Label>شماره چک</Label>
                  <Input value={chequeNo} onChange={(e) => setChequeNo(e.target.value)} dir="ltr" className="font-mono" />
                </div>
              )}

              {mode === 'chequebook' && (
                <div className="space-y-2">
                  <Label>سریال شروع دسته‌چک</Label>
                  <Input value={indexChequeNo} onChange={(e) => setIndexChequeNo(e.target.value)} dir="ltr" className="font-mono" />
                </div>
              )}

              {mode === 'amount' && (
                <div className="space-y-2">
                  <Label>مبلغ چک (ریال)</Label>
                  <Input value={amount} onChange={(e) => setAmount(formatAmountInput(e.target.value))} dir="ltr" className="font-mono" />
                </div>
              )}

              <div className="flex flex-wrap gap-2 md:col-span-2">
                <Button type="button" variant="outline" onClick={() => void loadForm(mode)} disabled={loadingForm}>
                  {loadingForm ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  <span className="mr-1">بروزرسانی فرم</span>
                </Button>
                <Button type="button" onClick={() => void handleSubmit()} disabled={submitting}>
                  {submitting ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                  اجرا
                </Button>
              </div>
            </div>

            {result && (
              <div className="space-y-3 rounded-md border bg-slate-50 px-3 py-3">
                {result.status && <p className="text-sm font-medium">{result.status}</p>}
                {fields.length > 0 && (
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {fields.map(([key, value]) => (
                      <div key={key} className="rounded border bg-white px-3 py-2 text-sm">
                        <div className="text-xs text-slate-500">{key}</div>
                        <div>{value || '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
                {result.raw && fields.length === 0 && (
                  <div className="space-y-2">
                    <Label>خروجی خام بانک</Label>
                    <Textarea value={result.raw} readOnly className="min-h-[120px] font-mono text-xs" dir="ltr" />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BsiChequeTab;
